import {
  $isTextNode,
  type DOMChildConversion,
  type DOMConversionOutput,
  type DOMExportOutputMap,
  type HTMLConfig,
} from "lexical";
import { useMemo } from "react";
import type {
  Settings,
  VariableMode,
} from "../context/SettingsContext/settings";
import { VariableNode } from "../nodes/VariableNode";
import { isVariableNode } from "../plugins/VariablesPlugin/utils";

const INLINE_TEXT_STYLES = [
  "background-color",
  "color",
  "font-family",
  "font-size",
] as const;

function convertStyledSpan(
  element: HTMLSpanElement
): DOMConversionOutput | null {
  const style = INLINE_TEXT_STYLES.map((property) => {
    const value = element.style.getPropertyValue(property);
    return value ? `${property}: ${value}` : "";
  })
    .filter(Boolean)
    .join("; ");

  if (!style) return null;

  const applyStyle: DOMChildConversion = (node) => {
    if ($isTextNode(node)) node.setStyle(style);
    return node;
  };

  return {
    forChild: applyStyle,
    node: null,
  };
}

export function useHTMLConfig(
  options: { variableMode: VariableMode } & Partial<Settings>
) {
  const htmlConfig: HTMLConfig = useMemo(() => {
    const expandedMustache = options.variableMode === "expanded";
    const variableExport: DOMExportOutputMap | undefined =
      options.enableVariables === false
        ? undefined
        : new Map([
            [
              VariableNode,
              (editor, target) => {
                if (isVariableNode(target)) {
                  return {
                    element: target.exportHTML(editor, expandedMustache)
                      .element,
                  };
                }
                return target.exportDOM(editor);
              },
            ],
          ]);

    return {
      import: {
        span: (element) => ({
          conversion: convertStyledSpan,
          priority: 1,
        }),
      },
      export: variableExport,
    };
  }, [options.enableVariables, options.variableMode]);

  return htmlConfig;
}
